import React, {useMemo, useState, useEffect} from 'react'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'

import {Box, Link, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {buildGoogleItineraryUrl} from '../../utils/url-utils'
import useMultiSite from '../../hooks/use-multi-site'
import {useGTM} from '@app/hooks/use-gtm'
import {formatHourByLocale} from '../../utils/utils'
import {STORE_STATUSES} from '@app/constants'

/**
 * StoreInfowindow component displays detailed information about a store.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.store - The store object containing information to display.
 * @param {string} props.store.city - The city where the store is located.
 * @param {string} props.store.address1 - The primary address of the store.
 * @param {string} [props.store.address2] - The secondary address of the store, if available.
 * @param {string} props.store.postalCode - The postal code of the store's location.
 * @param {string} props.store.countryCode - The country code of the store's location.
 * @param {string} props.store.phone - The phone number of the store.
 * @param {string} [props.store.c_WhatsappLink] - The WhatsApp link for the store, if available.
 * @param {string} [props.store.c_storeOpeningHours] - JSON string of the store's opening hours.
 * @param {number} props.store.latitude - The latitude coordinate of the store.
 * @param {number} props.store.longitude - The longitude coordinate of the store.
 * @returns {JSX.Element} The rendered component.
 */
const StoreInfowindow = ({store}) => {
    const {locale} = useMultiSite()
    const {formatMessage} = useIntl()
    const [isStoreOpened, setIsStoreOpened] = useState(true)
    const [isStoreTemporarilyClosed, setIsStoreTemporarilyClosed] = useState(false)
    const today = new Date().toLocaleString('en', {weekday: 'long'}).toLowerCase()
    const {triggerClickStoreLocatorEvent} = useGTM()

    const openingHours = useMemo(() => {
        try {
            return store.c_storeOpeningHours ? JSON.parse(store.c_storeOpeningHours) : null
        } catch (error) {
            console.error('Error parsing store opening hours:', error)
            return null
        }
    }, [store.c_storeOpeningHours])

    useEffect(() => {
        const isFutureOpening =
            store?.c_openingDate && new Date(store.c_openingDate).getTime() > Date.now()
        const isPastClosed =
            store?.c_closingDate && new Date(store.c_closingDate).getTime() < Date.now()

        if (
            openingHours?.[today]?.isClosed ||
            store.c_temporaryClosed ||
            store.c_seasonallyClosed ||
            isFutureOpening ||
            isPastClosed
        ) {
            setIsStoreOpened(false)
        }
        setIsStoreTemporarilyClosed(store.c_temporaryClosed)
    }, [openingHours, store.c_temporaryClosed, store.c_seasonallyClosed])

    const daysOfWeek = [
        {
            id: 'monday',
            label: formatMessage({
                id: `stores_page.store.monday`,
                defaultMessage: `Monday:`
            })
        },
        {
            id: 'tuesday',
            label: formatMessage({
                id: `stores_page.store.tuesday`,
                defaultMessage: `Tuesday:`
            })
        },
        {
            id: 'wednesday',
            label: formatMessage({
                id: `stores_page.store.wednesday`,
                defaultMessage: `Wednesday:`
            })
        },
        {
            id: 'thursday',
            label: formatMessage({
                id: `stores_page.store.thursday`,
                defaultMessage: `Thursday:`
            })
        },
        {
            id: 'friday',
            label: formatMessage({
                id: `stores_page.store.friday`,
                defaultMessage: `Friday:`
            })
        },
        {
            id: 'saturday',
            label: formatMessage({
                id: `stores_page.store.saturday`,
                defaultMessage: `Saturday:`
            })
        },
        {
            id: 'sunday',
            label: formatMessage({
                id: `stores_page.store.sunday`,
                defaultMessage: `Sunday:`
            })
        }
    ]

    const handleOnClick = (e, store) => {
        triggerClickStoreLocatorEvent(store.id, store.name || '')
    }

    return (
        <Box>
            {store.name && (
                <Text textTransform={'uppercase'} mb={'5px'}>
                    {store.name}
                </Text>
            )}
            <Box mb={'10px'}>
                <Text variant={'bodySmall'}>{store.address1}</Text>
                {store.address2 && <Text variant={'bodySmall'}>{store.address2}</Text>}
                <Text variant={'bodySmall'}>
                    {store.postalCode} {store.city}
                </Text>
                <Text variant={'bodySmall'}>{store.countryCode}</Text>
            </Box>
            <Box mb={'10px'}>
                <Link
                    mb={'5px'}
                    target="_blank"
                    href={`tel:${store.phone}`}
                    onClick={(e) => handleOnClick(e, store)}
                >
                    {formatMessage({
                        id: 'stores_page.store.call',
                        defaultMessage: 'Call'
                    })}{' '}
                    {store.phone}
                </Link>

                {store.c_WhatsappLink && (
                    <Link target="_blank" href={store.c_WhatsappLink}>
                        {formatMessage({
                            id: 'stores_page.store.whatsapp',
                            defaultMessage: 'WhatsApp'
                        })}
                    </Link>
                )}
            </Box>

            {/* Closed information */}
            {!isStoreOpened && (
                <Box mb={'10px'}>
                    <Text variant={'bodySmall'} color={'red'}>
                        {isStoreTemporarilyClosed
                            ? formatMessage(STORE_STATUSES.TEMPORARILY_CLOSED)
                            : formatMessage(STORE_STATUSES.CLOSED)}
                    </Text>
                </Box>
            )}

            {/* Open information */}
            {isStoreOpened && openingHours && (
                <Box mb={'10px'}>
                    {daysOfWeek.map((day) => (
                        <Text variant={'bodySmall'} mb={'5px'} key={day.id}>
                            {day.label}{' '}
                            {openingHours[day.id]?.isClosed
                                ? formatMessage({
                                      id: 'stores_page.store.hours.closed',
                                      defaultMessage: 'Closed'
                                  })
                                : openingHours[day.id]?.openIntervals
                                      ?.map((interval) => {
                                          const start = interval.start
                                          const end = interval.end

                                          return start && end
                                              ? `${formatHourByLocale(
                                                    locale.id,
                                                    start
                                                )} - ${formatHourByLocale(locale.id, end)}`
                                              : null
                                      })
                                      .filter(Boolean)
                                      .join('; ')}
                        </Text>
                    ))}
                </Box>
            )}

            <Box>
                <Link
                    href={buildGoogleItineraryUrl(store.latitude, store.longitude)}
                    target="_blank"
                >
                    {formatMessage({
                        id: 'stores_page.store.direction',
                        defaultMessage: 'Get direction'
                    })}
                </Link>
            </Box>
        </Box>
    )
}

StoreInfowindow.propTypes = {
    store: PropTypes.object
}

export default StoreInfowindow
